import React from 'react';
import { Alert, Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { AttachMoney } from '@mui/icons-material';

interface BasePriceExplanationCardProps {
  basePrice: number;
  includedUsers: number;
  isGestao: boolean;
  isInstitucional: boolean;
  formatCurrency: (value: number) => string;
}

/**
 * Card explicativo da mensalidade fixa (o que está incluso) — texto varia conforme o plano.
 * Extraído de UnifiedPricingCalculator.tsx (F7).
 */
export const BasePriceExplanationCard: React.FC<BasePriceExplanationCardProps> = ({
  basePrice,
  includedUsers,
  isGestao,
  isInstitucional,
  formatCurrency,
}) => {
  return (
    <Grid item xs={12}>
      <Card
        variant="outlined"
        sx={{
          bgcolor: 'primary.main',
          borderColor: 'primary.dark',
          borderWidth: 2,
          color: 'white',
        }}
      >
        <CardContent sx={{ color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AttachMoney sx={{ color: 'white', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              Mensalidade fixa: {formatCurrency(basePrice)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
            A mensalidade fixa é um valor mensal pago pelo contratante da VOVER e inclui:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1, color: 'white' }}>
              <strong>Acesso completo à plataforma</strong>
              {isGestao && ' para gerenciar todos os seus condomínios'}
              {isInstitucional && ' para gerenciar todos os seus núcleos'}
              {!isGestao && !isInstitucional && ' para gerenciar todos os seus condomínios'}
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, color: 'white' }}>
              <strong>Usuários incluídos</strong> ({includedUsers} usuários incluídos na assinatura) com possibilidade de contratar mais usuários conforme necessário
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, color: 'white' }}>
              <strong>Infraestrutura e suporte</strong> técnico da plataforma
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, color: 'white' }}>
              <strong>Atualizações e melhorias</strong> contínuas do sistema
            </Typography>
            <Typography component="li" variant="body2" sx={{ color: 'white' }}>
              <strong>Segurança e backup</strong> dos seus dados
            </Typography>
          </Box>
          <Alert
            severity="info"
            sx={{
              mt: 2,
              backgroundColor: 'rgba(255,255,255,0.12)',
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' },
              '& .MuiAlert-message': { color: 'white' },
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            <Typography variant="body2" component="span" sx={{ color: 'white' }}>
              <strong>Importante:</strong> A mensalidade fixa é única por conta (contratante da VOVER) e não se multiplica por condomínio. Você paga apenas uma vez, independente de quantos condomínios você gerencia.
            </Typography>
            {!isGestao && !isInstitucional && (
              <>
                <Typography variant="body2" sx={{ mt: 1, display: 'block', color: 'white' }}>
                  O preço de cada condomínio é calculado individualmente com base no número de unidades.
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, display: 'block', color: 'white' }}>
                  Utilize o nosso simulador de custos para saber o preço de cada condomínio.
                </Typography>
              </>
            )}
            {isGestao && (
              <Typography variant="body2" sx={{ mt: 1, display: 'block', color: 'white' }}>
                O valor da capacidade de condomínios é definido pelo pacote escolhido (ou quantidade personalizada). Utilize o simulador abaixo para ver o investimento total.
              </Typography>
            )}
            {isInstitucional && (
              <Typography variant="body2" sx={{ mt: 1, display: 'block', color: 'white' }}>
                O valor dos núcleos é definido pelo pacote de capacidade escolhido (ou quantidade personalizada). Utilize o simulador abaixo para ver o investimento total.
              </Typography>
            )}
          </Alert>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default BasePriceExplanationCard;
